// src/components/practices/MagicRock.js
// The interactive companion to the offline Magic Rock ritual (hold a rock each night, think of
// the best thing that happened). This is a distinct thing from Journal.js's existing static
// arrival-screen copy (.arrival-rock-intro on day 2, .arrival-rock-reminder on days 3-28) —
// that copy points at the same physical ritual but has no interactive flow behind it. Don't
// conflate the two when searching the codebase for "Magic Rock."
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import PageHeader from '../ui/PageHeader'
import PrimaryButton from '../ui/PrimaryButton'
import PracticeThankYouTap from './shared/PracticeThankYouTap'
import PracticeComplete from './shared/PracticeComplete'
import './shared/PracticeHeading.css'

const REREAD_MS = 2000

export default function MagicRock({ practice, session, city, onComplete, onBack, augustTheme, onAugustThemeChange }) {
  const [text, setText] = useState('')
  const [thanks, setThanks] = useState(0)
  const [reread, setReread] = useState(false)
  const [done, setDone] = useState(false)
  const [saving, setSaving] = useState(false)
  const rereadTimer = useRef(null)

  useEffect(() => () => clearTimeout(rereadTimer.current), [])

  const typed = text.trim().length > 2
  const isReady = typed && thanks >= 3

  const tapThanks = () => {
    if (thanks >= 3) return
    const next = thanks + 1
    setThanks(next)
    if (next < 3) {
      setReread(true)
      clearTimeout(rereadTimer.current)
      rereadTimer.current = setTimeout(() => setReread(false), REREAD_MS)
    }
  }

  const handleFinish = async () => {
    if (!isReady || saving) return
    setSaving(true)
    if (session) {
      // Append-only, same convention as Journal.js's practice_sessions insert — every
      // completion is a new row, never an overwrite.
      await supabase.from('practice_completions').insert({
        user_id: session.user.id,
        city: city || 'august',
        practice_id: practice.id,
        payload: { text },
      })
    }
    setSaving(false)
    setDone(true)
  }

  if (done) {
    return (
      <PracticeComplete
        headline="Practice Complete"
        subline="Your rock is holding onto something good."
        onDone={onComplete}
      />
    )
  }

  return (
    <div className="practice-screen" data-city="august">
      <PageHeader
        eyebrow={`Day ${practice.unlockDay} · ${practice.name}`} title="Best Thing Today"
        showBack onBack={onBack}
        augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
      />
      <div className="practice-screen-body">
        <p className="practice-question">What was the best thing that happened today?</p>

        <div className="stops-input-row">
          <textarea
            className="stops-input"
            placeholder="I'm grateful for…"
            value={text}
            onChange={e => setText(e.target.value)}
          />
        </div>

        <PracticeThankYouTap
          count={thanks}
          reread={reread}
          canTap={typed}
          onTap={tapThanks}
          writtenText={text}
        />

        <PrimaryButton onClick={isReady ? handleFinish : undefined} disabled={!isReady || saving}>
          <span>Done</span>
          <span>✦</span>
        </PrimaryButton>
      </div>
    </div>
  )
}
